// Drives the "Report access issue" drawer: single-select location/EIN picker
// with a takeover-style search results panel, plus a "Use current time" button
// that fills the When-did-it-happen pickers in the member's local timezone.
// Also reused by the SCA case form (partners/provider_access_cases/new), which
// mounts it without the time-picker targets — month/day/etc. are optional.
//
// State model:
//   - No selection + empty search → idle (results region hidden, form hidden).
//   - No selection + non-empty search → takeover (results region visible, form hidden).
//   - Selection → form mode (results hidden, form visible, selected card visible).
//
// Mounted on the drawer body root (see partner_access_issues/new.html.erb).
import { Controller } from "@hotwired/stimulus";

export default class extends Controller {
  static targets = [
    "searchInput",
    "targetScope",
    "targetId",
    "selectedCard",
    "selectedSimpleCard",
    "selectedDetails",
    "selectedTitle",
    "selectedSubtitle",
    "resultsRegion",
    "formSection",
    "month",
    "day",
    "year",
    "hour",
    "minute",
    "ampm",
    "submit",
  ];

  static values = {
    memberTz: String,
    selectUrl: String,
  };

  connect() {
    this.refresh();
  }

  // ---- State transitions ------------------------------------------------

  onSearchInput() {
    // Typing in the search box implies the user wants to find something
    // different — drop any prior selection and show the takeover.
    if (this.hasSelection()) this.clearSelectionInternal();
    this.refresh();
  }

  selectEntity(event) {
    // Stimulus matches keydown.space before the browser scrolls; preventDefault
    // stops the page jump when a result is "clicked" via keyboard.
    if (event.type === "keydown") event.preventDefault();
    const btn = event.currentTarget;
    const scope = btn.dataset.scope;
    const id = btn.dataset.targetId;
    const title = btn.dataset.title || "";
    const subtitle = btn.dataset.subtitle || "";

    this.targetScopeTarget.value = scope;
    this.targetIdTarget.value = id;
    this.selectedTitleTarget.textContent = title;
    this.selectedSubtitleTarget.textContent = subtitle;

    // Lock the search input so it's clear that the selection is "sticky".
    this.searchInputTarget.value = "";
    this.searchInputTarget.disabled = true;

    // Default the When-pickers to current time the first time a selection
    // happens (don't clobber if user already edited them).
    if (!this.timeFilled()) this.useCurrentTime();

    // For locations, fetch enriched details via Turbo.
    if (scope === "location" && this.selectUrlValue) {
      this.fetchLocationDetails(id);
    } else {
      // EIN or fallback: show simple card
      this.showSimpleCard();
    }

    this.refresh();
  }

  clearSelection(event) {
    if (event) event.preventDefault();
    this.clearSelectionInternal();
    this.refresh();
    // Re-focus the search box so the user can immediately type again.
    this.searchInputTarget.focus();
  }

  clearSelectionInternal() {
    this.targetScopeTarget.value = "";
    this.targetIdTarget.value = "";
    this.selectedTitleTarget.textContent = "";
    this.selectedSubtitleTarget.textContent = "";
    this.searchInputTarget.disabled = false;

    // Reset location details
    if (this.hasSelectedDetailsTarget) {
      this.selectedDetailsTarget.innerHTML = "";
    }
    if (this.hasSelectedSimpleCardTarget) {
      this.selectedSimpleCardTarget.classList.add("hidden");
    }
  }

  hasSelection() {
    return this.targetIdTarget.value !== "";
  }

  hasSearchQuery() {
    return this.searchInputTarget.value.trim() !== "";
  }

  refresh() {
    const selected = this.hasSelection();
    const searching = !selected && this.hasSearchQuery();

    this.toggle(this.selectedCardTarget, selected);
    this.toggle(
      this.resultsRegionTarget,
      searching || (!selected && this.resultsHavePopulated()),
    );
    this.toggleFlex(this.formSectionTarget, selected);

    this.submitTarget.disabled = !(selected && this.timeFilled());
  }

  showSimpleCard() {
    if (this.hasSelectedSimpleCardTarget) {
      this.selectedSimpleCardTarget.classList.remove("hidden");
    }
    if (this.hasSelectedDetailsTarget) {
      this.selectedDetailsTarget.innerHTML = "";
    }
  }

  async fetchLocationDetails(locationId) {
    const url = `${this.selectUrlValue}?target_id=${locationId}`;
    const csrfToken =
      document.querySelector('meta[name="csrf-token"]')?.content || "";
    try {
      const response = await fetch(url, {
        headers: {
          Accept: "text/vnd.turbo-stream.html",
          "X-Requested-With": "XMLHttpRequest",
          "X-CSRF-Token": csrfToken,
        },
      });
      if (response.ok) {
        const html = await response.text();
        // Hide simple card, show enriched details via Turbo
        if (this.hasSelectedSimpleCardTarget) {
          this.selectedSimpleCardTarget.classList.add("hidden");
        }
        window.Turbo.renderStreamMessage(html);
      } else {
        // Fallback to simple card on error
        this.showSimpleCard();
      }
    } catch {
      this.showSimpleCard();
    }
  }

  // True if the search-results partial has rendered any actual content
  // (e.g. the initial "Start typing..." placeholder or a No-results message).
  // We use this so the placeholder is visible from the start of search activity.
  resultsHavePopulated() {
    return this.hasSearchQuery();
  }

  toggle(el, show) {
    if (!el) return;
    el.classList.toggle("hidden", !show);
  }

  toggleFlex(el, show) {
    if (!el) return;
    el.classList.toggle("hidden", !show);
    el.classList.toggle("flex", show);
  }

  // ---- Time pickers -----------------------------------------------------

  useCurrentTime() {
    if (!this.hasMonthTarget) return;
    const tz = this.memberTzValue || undefined;
    // Format current time in the member's tz, then parse the pieces back out.
    const parts = new Intl.DateTimeFormat("en-US", {
      timeZone: tz,
      year: "numeric",
      month: "2-digit",
      day: "2-digit",
      hour: "numeric",
      minute: "2-digit",
      hour12: true,
    })
      .formatToParts(new Date())
      .reduce((acc, p) => {
        acc[p.type] = p.value;
        return acc;
      }, {});

    this.monthTarget.value = parts.month;
    this.dayTarget.value = parts.day;
    // Year option list only has current and prior year — if for some reason
    // the formatted year isn't an option, the assignment is a no-op.
    this.yearTarget.value = parts.year;

    let hour = String(parseInt(parts.hour, 10));
    if (!Array.from(this.hourTarget.options).some((o) => o.value === hour)) {
      // 12-hour clock: '0' shouldn't happen, but guard anyway.
      hour = "12";
    }
    this.hourTarget.value = hour;

    // Snap minute down to the nearest 15.
    const minute = parseInt(parts.minute, 10);
    const snapped = String(Math.floor(minute / 15) * 15).padStart(2, "0");
    this.minuteTarget.value = snapped;

    this.ampmTarget.value =
      parts.dayPeriod || (parts.hour >= 12 ? "PM" : "AM");

    this.refresh();
  }

  timeFilled() {
    // The SCA case form reuses this controller without the When-pickers;
    // treat time as satisfied when the targets aren't on the page.
    if (!this.hasMonthTarget) return true;
    return (
      this.monthTarget.value &&
      this.dayTarget.value &&
      this.yearTarget.value &&
      this.hourTarget.value &&
      this.minuteTarget.value &&
      this.ampmTarget.value
    );
  }
}
